import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { FramedImage } from "../components/ui/FramedImage";
import { useAuth } from "../App";
import { 
  Gavel, Clock, Users, Eye, EyeOff, ArrowRight, 
  Calendar, Timer, TrendingUp, Lock
} from "lucide-react";
import { API } from "../apiConfig";


// Countdown Timer Component
const CountdownTimer = ({ endTime, onEnd }) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const end = new Date(endTime).getTime();
      const now = new Date().getTime();
      const difference = end - now;

      if (difference <= 0) {
        onEnd?.();
        return null;
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [endTime, onEnd]);

  if (!timeLeft) {
    return <span className="text-destructive font-medium">Ended</span>;
  }

  return (
    <div className="flex items-center gap-1 text-sm">
      {timeLeft.days > 0 && (
        <span className="bg-primary/10 text-primary px-2 py-1 rounded font-mono">
          {timeLeft.days}d
        </span>
      )}
      <span className="bg-primary/10 text-primary px-2 py-1 rounded font-mono">
        {String(timeLeft.hours).padStart(2, '0')}h
      </span>
      <span className="bg-primary/10 text-primary px-2 py-1 rounded font-mono">
        {String(timeLeft.minutes).padStart(2, '0')}m
      </span>
      <span className="bg-primary/10 text-primary px-2 py-1 rounded font-mono">
        {String(timeLeft.seconds).padStart(2, '0')}s
      </span>
    </div>
  );
};

// Auction Card Component
const AuctionCard = ({ auction, isCollector }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { label: "Live Now", className: "bg-green-500 text-white" },
      scheduled: { label: "Coming Soon", className: "bg-blue-500 text-white" },
      ended: { label: "Ended", className: "bg-muted text-muted-foreground" },
      sold: { label: "Sold", className: "bg-primary text-primary-foreground" },
      no_sale: { label: "No Sale", className: "bg-muted text-muted-foreground" },
    };
    return badges[status] || { label: status, className: "bg-muted" };
  };

  const statusBadge = getStatusBadge(auction.status);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3 }}
    >
      <Link to={`/auctions/${auction.auction_id}`}>
        <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 border-border/50 group">
          {/* Image */}
          <div className="relative aspect-[4/5] bg-muted/30">
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <FramedImage
                src={auction.product_images?.[0] || "/placeholder.jpg"}
                alt={auction.product_title}
                frameScale={0.4}
                className="transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            
            {/* Status Badge */}
            <Badge className={`absolute top-4 left-4 ${statusBadge.className}`}>
              {statusBadge.label}
            </Badge>

            {/* Visibility Indicator */}
            <div className="absolute top-4 right-4">
              {auction.visibility === "silent" ? (
                <div className="bg-black/50 backdrop-blur-sm p-2 rounded-full" title="Silent Auction">
                  <EyeOff className="h-4 w-4 text-white" />
                </div>
              ) : (
                <div className="bg-white/50 backdrop-blur-sm p-2 rounded-full" title="Public Bids">
                  <Eye className="h-4 w-4 text-foreground" />
                </div>
              )}
            </div>

            {/* Live indicator for active auctions */}
            {auction.status === "active" && (
              <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-full">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                <span className="text-white text-xs font-medium">LIVE</span>
              </div>
            )}
          </div>

          <CardContent className="p-5">
            {/* Category */}
            <p className="text-xs tracking-widest uppercase text-primary mb-1">
              {auction.category?.replace("_", " ")}
            </p>

            {/* Title */}
            <h3 className="text-lg font-medium mb-2 line-clamp-2 group-hover:text-primary transition-colors">
              {auction.title}
            </h3>

            {/* Bidding Info - Blurred for non-collectors */}
            <div className="space-y-3 relative">
              {!isCollector && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-[2px] rounded-lg">
                  <div className="text-center px-2">
                    <Lock className="h-4 w-4 mx-auto mb-1 text-primary" />
                    <p className="text-xs text-muted-foreground">Sign in to view</p>
                  </div>
                </div>
              )}
              
              {/* Current Bid or Starting Bid */}
              <div className={`flex justify-between items-baseline ${!isCollector ? 'blur-sm' : ''}`}>
                <span className="text-sm text-muted-foreground">
                  {auction.current_bid > 0 ? "Current Bid" : "Starting Bid"}
                </span>
                <span className="text-xl font-semibold text-primary">
                  {formatPrice(auction.current_bid > 0 ? auction.current_bid : auction.starting_bid)}
                </span>
              </div>

              {/* Bid Count */}
              <div className={`flex items-center gap-2 text-sm text-muted-foreground ${!isCollector ? 'blur-sm' : ''}`}>
                <Users className="h-4 w-4" />
                <span>{auction.bid_count} bid{auction.bid_count !== 1 ? 's' : ''}</span>
                {auction.reserve_met && auction.current_bid > 0 && (
                  <Badge variant="outline" className="ml-auto text-xs text-green-600 border-green-600">
                    Reserve Met
                  </Badge>
                )}
              </div>

              {/* Time Remaining */}
              {auction.status === "active" && (
                <div className={`flex items-center gap-2 pt-2 border-t border-border/50 ${!isCollector ? 'blur-sm' : ''}`}>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <CountdownTimer endTime={auction.end_time} />
                </div>
              )}

              {auction.status === "scheduled" && (
                <div className="flex items-center gap-2 pt-2 border-t border-border/50 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>Starts {new Date(auction.start_time).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
};

const AuctionsPage = () => {
  const { user } = useAuth();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  
  // Collector status - user must be logged in
  const isCollector = !!user;

  useEffect(() => {
    fetchAuctions();
  }, []);

  const fetchAuctions = async () => {
    try {
      const response = await axios.get(`${API}/auctions`);
      setAuctions(response.data);
    } catch (error) {
      console.error("Error fetching auctions:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAuctions = useCallback(() => {
    if (activeTab === "all") return auctions;
    if (activeTab === "active") return auctions.filter(a => a.status === "active");
    if (activeTab === "upcoming") return auctions.filter(a => a.status === "scheduled");
    if (activeTab === "ended") return auctions.filter(a => ["ended", "sold", "no_sale"].includes(a.status));
    return auctions;
  }, [auctions, activeTab]);

  return (
    <div className="min-h-screen" data-testid="auctions-page">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6 md:px-12">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="relative max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Badge variant="outline" className="mb-4 text-sm tracking-widest uppercase border-primary/30 text-primary">
              Silent Auctions
            </Badge>
            <h1 className="text-5xl md:text-6xl font-medium mb-4">
              Exclusive Artworks
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Bid on rare original artworks, kangaroo skins, and special pieces by Colin Isaacs. 
              Each auction is your chance to own a unique piece of Aboriginal heritage.
            </p>
          </motion.div>

          {/* How It Works */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 max-w-4xl mx-auto"
          >
            <div className="flex flex-col items-center p-6 bg-card rounded-2xl border border-border/50">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Lock className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-medium mb-2">Silent Bidding</h3>
              <p className="text-sm text-muted-foreground text-center">
                Bids are kept private until the auction ends
              </p>
            </div>
            <div className="flex flex-col items-center p-6 bg-card rounded-2xl border border-border/50">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Timer className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-medium mb-2">Anti-Sniping</h3>
              <p className="text-sm text-muted-foreground text-center">
                Last-minute bids extend the auction by 5 minutes
              </p>
            </div>
            <div className="flex flex-col items-center p-6 bg-card rounded-2xl border border-border/50">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-medium mb-2">Fair Pricing</h3>
              <p className="text-sm text-muted-foreground text-center">
                Reserve prices ensure fair value for rare pieces
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Auctions List */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-4">
              <TabsTrigger value="active" className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                Live
              </TabsTrigger>
              <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
              <TabsTrigger value="ended">Ended</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-muted rounded-2xl mb-4" />
                  <div className="h-4 bg-muted rounded w-1/4 mb-2" />
                  <div className="h-6 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : filteredAuctions().length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <Gavel className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-xl font-medium mb-2">No auctions found</h3>
              <p className="text-muted-foreground mb-6">
                {activeTab === "active" 
                  ? "There are no live auctions at the moment. Check back soon!"
                  : activeTab === "upcoming"
                  ? "No upcoming auctions scheduled. Stay tuned!"
                  : "No auctions match your filter."}
              </p>
              {activeTab !== "all" && (
                <Button variant="outline" onClick={() => setActiveTab("all")}>
                  View All Auctions
                </Button>
              )}
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredAuctions().map((auction, index) => (
                <AuctionCard key={auction.auction_id} auction={auction} isCollector={isCollector} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 bg-card/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-medium mb-4">
            Want to be notified of new auctions?
          </h2>
          <p className="text-muted-foreground mb-8">
            Sign up to receive alerts when new pieces become available for bidding.
          </p>
          <Link to="/register">
            <Button size="lg" className="rounded-full px-8">
              Create Account
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AuctionsPage;
